/**
 * TEMPORARY — per-environment sidebar allow-list.
 *
 * Superseded by role-based menu filtering (applicationFilter.ts), driven by the
 * OPUS application catalog delivered on the profile payload. Removal is
 * two-phase — first clear `menuVisibleKeys` in every Helm overlay (this becomes
 * a no-op with no code change), then delete this file and its call site in
 * MainSidebar.vue. The navigation-item types now live in applicationFilter.ts
 * (re-exported here for compatibility), so this file deletes cleanly.
 */
/**
 * Keeps only the entries named in `visibleKeys`.
 *
 * - `visibleKeys === undefined` → the input is returned as-is (no filtering).
 * - Section keys are NOT listable: a section is kept when at least one of its
 *   children is allow-listed, and only those children survive. This removes the
 *   "I listed the section but forgot its children, so it renders empty" trap.
 * - Unknown keys are ignored.
 * - An allow-list that matches NOTHING is treated as a configuration error, not
 *   as a valid result: the unfiltered menu is returned. A zero-entry sidebar
 *   strands the user with no navigation at all, and it is reachable from a single
 *   typo ("contact", "Documents" — the comparison is case-sensitive), from listing
 *   only section keys, or from a `navigationConfig` key renamed without the Helm
 *   overlays following (they live in a different repo). This mirrors the landing
 *   page half of the feature, which also falls back to the safe default when its
 *   value is unusable. Deliberately not signalled to the user: `logger.warn` is
 *   compiled out of production bundles, so degrading to the full menu is the only
 *   feedback an operator can actually get.
 *
 * Pure: never mutates `items`. Callers apply their capability/flag gates FIRST, so
 * this can only narrow the menu, never widen it — the no-match fallback returns
 * `items` itself, which is the already-gated set.
 */
export function filterNavigation(items, visibleKeys) {
    if (!visibleKeys) {
        return items;
    }
    const allowed = new Set(visibleKeys);
    const kept = items.reduce((acc, item) => {
        if (item.children?.length) {
            const children = item.children.filter((child) => allowed.has(child.key));
            if (children.length > 0) {
                acc.push({ ...item, children });
            }
            return acc;
        }
        if (allowed.has(item.key)) {
            acc.push(item);
        }
        return acc;
    }, []);
    // Unusable allow-list — an empty sidebar strands the user with no navigation,
    // and no cosmetic escape hatch (the "deep links keep working" promise needs a
    // link to click). Degrade to the unfiltered menu instead.
    return kept.length > 0 ? kept : items;
}
